import { api } from './client';

export type TaskStatus = 'todo' | 'in_progress' | 'waiting' | 'blocked' | 'review' | 'active' | 'done' | 'cancelled';
export type TaskPriority = 'low' | 'normal' | 'high' | 'urgent';
export type TaskRecurrence = 'none' | 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';
export type TaskVisibility = 'private' | 'space' | 'linked_spaces' | 'tenant' | 'restricted_users';
export type TaskRelationType = 'related' | 'requested_from' | 'blocked_by' | 'visible_to';
export type TaskCommentVisibility = 'task' | 'primary_space' | 'linked_spaces' | 'restricted_users';

export interface TaskListItem {
  id: number;
  tenantId: number;
  primarySpaceId: number;
  title: string;
  descriptionHtml: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  recurrence: TaskRecurrence;
  visibility: TaskVisibility;
  createdByUserId: number;
  assigneeUserId: number | null;
  projectId: number | null;
  parentTaskId?: number | null;
  dueAt: string | null;
  completedAt: string | null;
  createdAt?: string;
  subtaskCount?: number;
  subtaskDoneCount?: number;
  links: Array<{ spaceId: number; relationType: TaskRelationType }>;
  callerRelation: 'primary' | 'linked' | 'restricted';
}

export interface TaskCreatePayload {
  primarySpaceId: number;
  title: string;
  descriptionHtml?: string;
  status?: TaskStatus;
  priority?: TaskPriority;
  recurrence?: TaskRecurrence;
  visibility?: TaskVisibility;
  parentTaskId?: number | null;
  projectId?: number | null;
  assigneeUserId?: number | null;
  dueAt?: string | null;
  links?: Array<{ spaceId: number; relationType: TaskRelationType }>;
}

export interface TaskReaction {
  emoji: string;
  count: number;
  me: boolean;
}
export interface TaskComment {
  id: number;
  taskId: number;
  authorUserId: number | null;
  bodyHtml: string;
  visibility: TaskCommentVisibility;
  linkedSpaceId: number | null;
  createdAt: string;
  reactions?: TaskReaction[];
}

export interface TaskAttachment {
  id: number;
  taskId: number;
  uploadedByUserId: number | null;
  originalFilename: string;
  mimeType: string | null;
  sizeBytes: number | null;
  checksumSha256: string | null;
  createdAt: string;
}

// "Moje úkoly" stránka filtruje klientsky (mine/delegated/projekt) z plné sady,
// proto načítáme celou pracovní sadu, ne jen nejnovějších 100 (jinak migrované
// úkoly se starým created_at vypadnou z okna a uživatel vidí jen zlomek svých).
export function listTasks(limit = 1000): Promise<TaskListItem[]> {
  return api.get<TaskListItem[]>(`/api/v1/tasks?limit=${limit}`);
}

export function getTask(id: number): Promise<TaskListItem> {
  return api.get<TaskListItem>(`/api/v1/tasks/${id}`);
}

export function createTask(payload: TaskCreatePayload): Promise<TaskListItem> {
  return api.post<TaskListItem>('/api/v1/tasks', payload);
}

export interface TaskUpdatePayload {
  title?: string;
  descriptionHtml?: string | null;
  status?: TaskStatus;
  priority?: TaskPriority;
  recurrence?: TaskRecurrence;
  assigneeUserId?: number | null;
  projectId?: number | null;
  primarySpaceId?: number;
  dueAt?: string | null;
}

export interface TaskUser {
  id: number;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  avatarUrl: string | null;
}

export function listTaskUsers(): Promise<TaskUser[]> {
  return api.get<TaskUser[]>('/api/v1/tasks/users');
}

export type TaskNotificationType = 'assigned' | 'mentioned' | 'status_changed' | 'completed' | 'commented' | 'reacted';
export interface TaskNotification {
  id: number;
  type: TaskNotificationType;
  taskId: number;
  taskTitle: string | null;
  actorUserId: number | null;
  commentId: number | null;
  seenAt: string | null;
  createdAt: string;
}
export function listNotifications(): Promise<TaskNotification[]> {
  return api.get<TaskNotification[]>('/api/v1/tasks/notifications');
}
export function countNotifications(): Promise<{ count: number }> {
  return api.get<{ count: number }>('/api/v1/tasks/notifications/count');
}
export function markAllNotificationsSeen(): Promise<unknown> {
  return api.post('/api/v1/tasks/notifications/mark-all-seen');
}
export function markNotificationSeen(id: number): Promise<unknown> {
  return api.post(`/api/v1/tasks/notifications/${id}/seen`);
}

export function updateTask(id: number, patch: TaskUpdatePayload): Promise<TaskListItem> {
  return api.patch<TaskListItem>(`/api/v1/tasks/${id}`, patch);
}

export function listSubtasks(id: number): Promise<TaskListItem[]> {
  return api.get<TaskListItem[]>(`/api/v1/tasks/${id}/subtasks`);
}

export function searchTasks(q: string, includeDone = false): Promise<TaskListItem[]> {
  const p = new URLSearchParams({ q, includeDone: includeDone ? '1' : '0' });
  return api.get<TaskListItem[]>(`/api/v1/tasks/search?${p.toString()}`);
}

// Soft-archive (prod "Smazat úkol"): server stamps archived_at, the task
// disappears from normal lists but is never hard-deleted.
export function archiveTask(id: number): Promise<unknown> {
  return api.post(`/api/v1/tasks/${id}/archive`);
}

export interface TaskProject {
  id: number;
  tenantId: number;
  spaceId: number;
  name: string;
  position: number;
  tasksCount: number;
  doneCount: number;
}

export function listProjects(): Promise<TaskProject[]> {
  return api.get<TaskProject[]>('/api/v1/tasks/projects');
}

export function createProject(spaceId: number, name: string): Promise<TaskProject> {
  return api.post<TaskProject>('/api/v1/tasks/projects', { spaceId, name });
}

export function listTaskComments(taskId: number): Promise<TaskComment[]> {
  return api.get<TaskComment[]>(`/api/v1/tasks/${taskId}/comments`);
}

export function postTaskComment(
  taskId: number,
  body: { bodyHtml: string; visibility?: TaskCommentVisibility; linkedSpaceId?: number | null; mentionUserIds?: number[] },
): Promise<TaskComment> {
  return api.post<TaskComment>(`/api/v1/tasks/${taskId}/comments`, body);
}

export function reactToComment(taskId: number, commentId: number, emoji: string): Promise<TaskComment> {
  return api.post<TaskComment>(`/api/v1/tasks/${taskId}/comments/${commentId}/reactions`, { emoji });
}

export function listTaskAttachments(taskId: number): Promise<TaskAttachment[]> {
  return api.get<TaskAttachment[]>(`/api/v1/tasks/${taskId}/attachments`);
}

export function uploadTaskAttachment(
  taskId: number,
  file: { originalFilename: string; contentBase64: string; mimeType?: string | null },
): Promise<TaskAttachment> {
  return api.post<TaskAttachment>(`/api/v1/tasks/${taskId}/attachments`, file);
}

export function deleteTaskAttachment(taskId: number, attachmentId: number): Promise<void> {
  return api.delete<void>(`/api/v1/tasks/${taskId}/attachments/${attachmentId}`);
}
